import React, { useState, useRef, useCallback, useEffect } from 'react'

interface ResizableSplitterProps {
  onDrag: (newWidth: number) => void
  initialWidth: number
  minWidth?: number
  maxWidth?: number
  orientation?: 'vertical' | 'horizontal'
  isLeftSplitter?: boolean // Added prop to indicate if this is a left-side splitter
}

/**
 * 可拖动的分割器组件
 * 用于调整左右或上下区域的大小
 */
const ResizableSplitter: React.FC<ResizableSplitterProps> = ({
  onDrag,
  initialWidth,
  minWidth = 300,
  maxWidth = 800,
  orientation = 'vertical',
  isLeftSplitter = false // Default to false for backward compatibility
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [startX, setStartX] = useState(0)
  const [startWidth, setStartWidth] = useState(initialWidth)
  const splitterRef = useRef<HTMLDivElement>(null)

  // 处理鼠标按下事件
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true)
    setStartX(e.clientX)
    setStartWidth(initialWidth)
    
    // 防止文本选中
    e.preventDefault()
  }, [initialWidth])

  // 处理鼠标移动事件
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return

    // 正向计算：拖动方向与宽度变化一致
    // 对于左侧分割器（位于左侧面板右侧）：
    // - 向右拖动 → 左侧面板变宽
    // - 向左拖动 → 左侧面板变窄
    // 对于右侧分割器（位于右侧面板左侧）：
    // - 向右拖动 → 右侧面板变窄
    // - 向左拖动 → 右侧面板变宽
    const deltaX = e.clientX - startX
    let newWidth = isLeftSplitter ? startWidth + deltaX : startWidth - deltaX
    
    // 限制宽度在最小和最大值之间
    newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth))
    
    onDrag(newWidth)
  }, [isDragging, startX, startWidth, minWidth, maxWidth, onDrag, isLeftSplitter])

  // 处理鼠标抬起事件
  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  // 处理鼠标悬停
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false)
  }, [])

  // 添加全局事件监听器
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      
      // 改变鼠标样式
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      document.body.style.pointerEvents = 'none'
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      
      // 恢复鼠标样式
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      document.body.style.pointerEvents = ''
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  // 处理键盘事件（辅助功能）
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault()
      
      const step = e.shiftKey ? 50 : 10
      let newWidth = initialWidth
      
      if (isLeftSplitter) {
        // 左侧分割器：箭头方向直接对应宽度变化
        if (e.key === 'ArrowLeft') {
          newWidth = Math.max(minWidth, initialWidth - step) // 向左箭头 → 变窄
        } else if (e.key === 'ArrowRight') {
          newWidth = Math.min(maxWidth, initialWidth + step) // 向右箭头 → 变宽
        }
      } else {
        // 右侧分割器：箭头方向与宽度变化相反
        if (e.key === 'ArrowLeft') {
          newWidth = Math.min(maxWidth, initialWidth + step) // 向左箭头 → 变宽
        } else if (e.key === 'ArrowRight') {
          newWidth = Math.max(minWidth, initialWidth - step) // 向右箭头 → 变窄
        }
      }
      
      onDrag(newWidth)
    }
  }, [initialWidth, minWidth, maxWidth, onDrag, isLeftSplitter])

  const isActive = isDragging || isHovered

  return (
    <div
      ref={splitterRef}
      className={`
        relative flex items-center justify-center
        ${orientation === 'vertical' ? 'w-3 cursor-col-resize' : 'h-3 cursor-row-resize'}
        transition-all duration-150 z-30
        ${isActive ? 'bg-blue-200 dark:bg-blue-800' : 'bg-transparent hover:bg-gray-200 dark:hover:bg-gray-600'}
      `}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="slider"
      aria-valuenow={initialWidth}
      aria-valuemin={minWidth}
      aria-valuemax={maxWidth}
      aria-label="调整右参考编辑器宽度"
      title="拖动调整宽度 (或使用左右箭头键)"
    >
      {/* 分割器手柄 - 更明显的视觉指示 */}
      <div 
        className={`
          ${orientation === 'vertical' ? 'w-1 h-10' : 'h-1 w-10'}
          rounded-full transition-all duration-200
          ${isActive ? 'bg-blue-500 dark:bg-blue-400 scale-110' : 'bg-gray-400 dark:bg-gray-500'}
        `}
      />
      
      {/* 拖动时的视觉反馈 */}
      {isDragging && (
        <div className="absolute inset-0 bg-blue-300 dark:bg-blue-700 opacity-30" />
      )}
      
      {/* 悬停时的边框指示 */}
      {isHovered && !isDragging && (
        <div className="absolute inset-0 border-2 border-blue-400 dark:border-blue-300 opacity-50 rounded" />
      )}
    </div>
  )
}

export default ResizableSplitter